import {
  createDeviceElement,
  type DeviceElement,
  type DeviceProps,
  type DeviceTilt,
  deviceBoxSize,
  deviceCanRotate,
  deviceContentAspect,
  deviceForScreenshot,
  deviceModel,
  devicePerspectives,
  exactDevice,
  resizeDeviceBox,
  type Shadow,
} from "@cva/shared";
import { toast } from "sonner";
import { addElements } from "./commands";
import { activePageDoc, type EditorStore } from "./store";

/** Editor commands for device mockups (docs/19-mockups.md). */

export interface ScreenshotInfo {
  id: string;
  width: number | null;
  height: number | null;
  name?: string;
}

/** Soft shadow below the device, in proportion to its size. */
export function deviceShadow(width: number, height: number): Shadow {
  const side = Math.max(width, height);
  return {
    x: 0,
    y: Math.round(side * 0.025),
    blur: Math.round(side * 0.07),
    color: "#00000038",
  };
}

/** The preset of a view id for a device (unknown views of this kind: straight). */
export function tiltFor(device: string, view: string | null | undefined): DeviceTilt {
  const perspectives = devicePerspectives(device);
  return { ...(perspectives.find((entry) => entry.id === view) ?? perspectives[0]!).tilt };
}

/**
 * Adds a device mockup, centred (or at `at`), about 70 % of the page. With a
 * screenshot and no device, the device is chosen from the picture's size.
 */
export function addDevice(
  store: EditorStore,
  options: {
    device?: string;
    screenshot?: ScreenshotInfo | null;
    view?: string | null;
    finish?: string;
    landscape?: boolean;
    at?: { x: number; y: number };
  } = {},
): string {
  const doc = activePageDoc(store);
  const screenshot = options.screenshot ?? null;
  const device = options.device ?? deviceForScreenshot(screenshot).device;
  const landscape = options.landscape ?? landscapeFor(device, screenshot);
  const props: DeviceProps = {
    device,
    landscape,
    tilt: tiltFor(device, options.view),
    ...(options.finish ? { finish: options.finish } : {}),
  };
  const contentAspect = deviceContentAspect(screenshot);
  const size = deviceBoxSize(props, contentAspect, {
    width: doc.format.width * 0.7,
    height: doc.format.height * 0.7,
  });
  const center = options.at ?? { x: doc.format.width / 2, y: doc.format.height / 2 };
  const element = createDeviceElement({
    ...props,
    tilt: props.tilt ?? { x: 0, y: 0, z: 0 },
    x: Math.round(center.x - size.width / 2),
    y: Math.round(center.y - size.height / 2),
    ...size,
    shadow: deviceShadow(size.width, size.height),
    ...(screenshot ? { assetId: screenshot.id } : {}),
  });
  addElements(store, [element]);
  return element.id;
}

/** Phones and tablets stand in the screenshot's orientation. */
function landscapeFor(device: string, screenshot: ScreenshotInfo | null): boolean {
  if (!screenshot?.width || !screenshot.height || !deviceCanTurn(device)) return false;
  const { screen } = deviceModel(device);
  return screenshot.width > screenshot.height !== screen.width > screen.height;
}

function deviceCanTurn(device: string): boolean {
  return deviceCanRotate(deviceModel(device));
}

/**
 * Changes the view of device elements (model, colour, orientation, tilt,
 * address): the box keeps its centre and the device its size on the page.
 * `merge` groups slider drags into one undo step.
 */
export function updateDevices(
  store: EditorStore,
  ids: string[],
  change: Partial<Pick<DeviceElement, "device" | "finish" | "landscape" | "tilt" | "url">>,
  sizeOf: (assetId: string) => ScreenshotInfo | undefined,
  merge?: string,
) {
  store.getState().update(
    (draft) => {
      for (const id of ids) {
        const element = draft.elements[id];
        if (element?.type !== "device" || element.locked) continue;
        const before: DeviceElement = { ...element, tilt: { ...element.tilt } };
        const next = { ...before, ...change };
        if (change.device && change.device !== before.device) {
          // Another model: its own colours and views.
          delete next.finish;
          if (!deviceCanTurn(change.device)) next.landscape = false;
        }
        const content = deviceContentAspect(element.assetId ? sizeOf(element.assetId) : null);
        const box = resizeDeviceBox(before, next, content, content);
        Object.assign(element, next, box);
        if (!next.finish) delete element.finish;
        if (element.shadow && change.device && change.device !== before.device) {
          element.shadow = deviceShadow(box.width, box.height);
        }
      }
    },
    merge ? { merge } : undefined,
  );
}

/** Replaces an image element by a mockup showing it (same centre, similar size). */
export function imageToDevice(store: EditorStore, imageId: string, screenshot: ScreenshotInfo) {
  const { doc } = store.getState();
  const image = doc.elements[imageId];
  if (image?.type !== "image" || image.locked) return null;
  const { device, landscape } = deviceForScreenshot(screenshot);
  const props: DeviceProps = {
    device,
    landscape,
    tilt: { x: 0, y: 0, z: 0 },
  };
  const side = Math.max(image.width, image.height) * 1.04;
  const size = deviceBoxSize(props, deviceContentAspect(screenshot), { width: side, height: side });
  const element = createDeviceElement({
    ...props,
    tilt: { x: 0, y: 0, z: 0 },
    id: image.id,
    name: image.name,
    x: Math.round(image.x + image.width / 2 - size.width / 2),
    y: Math.round(image.y + image.height / 2 - size.height / 2),
    ...size,
    rotation: image.rotation,
    opacity: image.opacity,
    shadow: image.shadow ?? deviceShadow(size.width, size.height),
    assetId: screenshot.id,
    ...(image.time ? { time: image.time } : {}),
    ...(image.animation ? { animation: image.animation } : {}),
  });
  store.getState().update((draft) => {
    draft.elements[image.id] = element;
  });
  store.getState().select([image.id]);
  return image.id;
}

/**
 * A picture that is exactly a device's screenshot (e.g. 1206 × 2622 from an
 * iPhone 17 Pro) was inserted: offer to show it in that device.
 */
export function offerMockup(store: EditorStore, imageId: string, screenshot: ScreenshotInfo) {
  const model = exactDevice(screenshot.width, screenshot.height);
  if (!model) return;
  toast(`Screenshot vom ${model.name}?`, {
    description: "Mit einem Klick als Mockup im passenden Gerät zeigen.",
    action: {
      label: "Als Mockup",
      onClick: () => imageToDevice(store, imageId, screenshot),
    },
    duration: 8000,
  });
}
